import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Observable } from 'rxjs';

import { PartnerService } from '../interfaces';

import { Partner, ImageDescription, SocialMediaLink, PartnerResponse, TextSection, PartnerConfig, PartnerLogoImageResponse, PartnerAppConfigResponse, PartnerTextSectionsResponse, PartnerImageDescriptionsResponse, PartnerSocialMediaResponse, ContactInfo, PartnerData, PartnerContactInfoResponse, PartnerDataResponse, PartnerSettings, PartnerSettingsResponse } from '../models';
import { Guid } from '@fitscovery/common/types';

import { Environment } from '@fitscovery/common/environments';
import { APP_ENVIRONMENT } from '../environment-injection';

@Injectable()
export class ApiPartnerService implements PartnerService {

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_ENVIRONMENT) private env: Environment,
    private http: HttpClient,
    private transferState: TransferState
  ) { }

  async getPartnerBySubdomain(subdomain: string): Promise<Partner> {
    const PARTNER_KEY = makeStateKey(`partner-${subdomain}`);

    if (this.transferState.hasKey(PARTNER_KEY)) {
      const json = this.transferState.get<string>(PARTNER_KEY, '');
      this.transferState.remove(PARTNER_KEY);
      const partner: Partner = JSON.parse(json);
      return Promise.resolve(partner);
    } else {
      const partnerData: any = await this.http.get(`${this.env.azureFunctionsApi.partners}/partners/subdomain/${subdomain}`)
        .toPromise();

      if (isPlatformServer(this.platformId)) {
        this.transferState.set(PARTNER_KEY, partnerData.data[0]);
      }
      return partnerData.data[0];
    }
  }

  getPartnerById(id: Guid): Observable<PartnerResponse> {
    return <Observable<PartnerResponse>>this.http.get(`${this.env.azureFunctionsApi.partners}/partners/${id}`);
  }

  createPartnerWebsite(partnerData: PartnerData): Observable<PartnerDataResponse> {
    return <Observable<PartnerDataResponse>>this.http.post(`${this.env.azureFunctionsApi.partners}/partners/base`, partnerData);
  }

  updateLogoImage(partnerId: Guid, logoUrl: string): Observable<PartnerLogoImageResponse> {
    const postData = { partnerId, logoUrl };
    return <Observable<PartnerLogoImageResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${partnerId}/logo`, postData);
  }

  updateImageDescriptions(partnerId: Guid, imageDescriptions: ImageDescription[]): Observable<PartnerImageDescriptionsResponse> {
    return <Observable<PartnerImageDescriptionsResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${partnerId}/media`, imageDescriptions);
  }
  
  updateAppConfig(appConfig: PartnerConfig): Observable<PartnerAppConfigResponse> {
    return <Observable<PartnerAppConfigResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${appConfig.partnerId}/app-config`, appConfig);
  }

  updateTextSections(partnerId: Guid, textSections: TextSection[]): Observable<PartnerTextSectionsResponse> {
    return <Observable<PartnerTextSectionsResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${partnerId}/text-sections`, textSections);
  }

  updateSocialMedia(partnerId: Guid, socialMedia: SocialMediaLink[]): Observable<PartnerSocialMediaResponse> {
    return <Observable<PartnerSocialMediaResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${partnerId}/social-media-links`, socialMedia);
  }

  updateContactInfo(contactInfo: ContactInfo): Observable<PartnerContactInfoResponse> {
    return <Observable<PartnerContactInfoResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${contactInfo.partnerId}/contact-info`, contactInfo);
  }

  updatePartnerSettings(partnerId: Guid, settings: PartnerSettings): Observable<PartnerSettingsResponse> {
    return <Observable<PartnerSettingsResponse>>this.http.put(`${this.env.azureFunctionsApi.partners}/partners/${partnerId}/settings`, settings);
  }

}
